import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions, Table, TableHead, TableRow,
  TableCell, TableBody, Chip, CircularProgress, Alert, IconButton, Tooltip,
  TableContainer, MenuItem, Select, FormControl, InputLabel, Divider,
} from '@mui/material';
import { Add, Edit, Delete, LocalShipping, Refresh, Receipt, Check } from '@mui/icons-material';
import {
  fetchSuppliers, createSupplier, updateSupplier, deleteSupplier,
  fetchPurchaseOrders, createPurchaseOrder, receivePurchaseOrder, updatePOStatus,
  fetchProducts,
} from '../../api/api';

const KES = (n) => `KES ${Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`;
const fmt = (d) => d ? new Date(d).toLocaleDateString() : '—';

const PO_STATUS_COLORS = {
  draft: 'default',
  sent: 'info',
  partial: 'warning',
  received: 'success',
  cancelled: 'error',
};

const EMPTY_SUPPLIER = { name: '', contactPerson: '', phone: '', email: '', paymentTerms: '', active: true };

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

export default function SupplierManagement() {
  const [tab, setTab] = useState(0);

  /* Suppliers state */
  const [suppliers, setSuppliers] = useState([]);
  const [suppLoading, setSuppLoading] = useState(true);
  const [suppError, setSuppError] = useState('');

  /* Supplier dialog */
  const [suppDialogOpen, setSuppDialogOpen] = useState(false);
  const [suppEditTarget, setSuppEditTarget] = useState(null);
  const [suppForm, setSuppForm] = useState(EMPTY_SUPPLIER);
  const [suppSaveLoading, setSuppSaveLoading] = useState(false);
  const [suppSaveError, setSuppSaveError] = useState('');

  /* Delete supplier */
  const [deleteSuppTarget, setDeleteSuppTarget] = useState(null);
  const [deleteSuppLoading, setDeleteSuppLoading] = useState(false);

  /* Purchase Orders */
  const [pos, setPos] = useState([]);
  const [poLoading, setPoLoading] = useState(true);
  const [poError, setPoError] = useState('');
  const [poSuppFilter, setPoSuppFilter] = useState('');
  const [poStatusFilter, setPoStatusFilter] = useState('');

  /* Create PO dialog */
  const [poDialogOpen, setPoDialogOpen] = useState(false);
  const [poForm, setPoForm] = useState({ supplierId: '', notes: '', expectedDate: '', items: [{ productId: '', quantity: '', unitCost: '' }] });
  const [products, setProducts] = useState([]);
  const [poSaveLoading, setPoSaveLoading] = useState(false);
  const [poSaveError, setPoSaveError] = useState('');

  /* Receive PO dialog */
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [receivePO, setReceivePO] = useState(null);
  const [receiveItems, setReceiveItems] = useState([]);
  const [receiveLoading, setReceiveLoading] = useState(false);
  const [receiveError, setReceiveError] = useState('');

  useEffect(() => { loadSuppliers(); }, []);
  useEffect(() => { if (tab === 1) loadPOs(); }, [tab, poSuppFilter, poStatusFilter]);

  const loadSuppliers = async () => {
    try {
      setSuppLoading(true);
      setSuppError('');
      const res = await fetchSuppliers();
      setSuppliers(res.data?.suppliers || res.data || []);
    } catch (err) {
      setSuppError(err.response?.data?.message || 'Failed to load suppliers.');
    } finally {
      setSuppLoading(false);
    }
  };

  const loadPOs = async () => {
    try {
      setPoLoading(true);
      setPoError('');
      const params = {};
      if (poSuppFilter) params.supplierId = poSuppFilter;
      if (poStatusFilter) params.status = poStatusFilter;
      const res = await fetchPurchaseOrders(params);
      setPos(res.data?.orders || res.data?.purchaseOrders || res.data || []);
    } catch (err) {
      setPoError(err.response?.data?.message || 'Failed to load purchase orders.');
    } finally {
      setPoLoading(false);
    }
  };

  /* ── supplier CRUD ── */
  const openCreateSupplier = () => { setSuppEditTarget(null); setSuppForm(EMPTY_SUPPLIER); setSuppSaveError(''); setSuppDialogOpen(true); };
  const openEditSupplier = (s) => {
    setSuppEditTarget(s);
    setSuppForm({ name: s.name || '', contactPerson: s.contactPerson || '', phone: s.phone || '', email: s.email || '', paymentTerms: s.paymentTerms || '', active: s.active !== false });
    setSuppSaveError('');
    setSuppDialogOpen(true);
  };

  const handleSaveSupplier = async () => {
    if (!suppForm.name.trim()) { setSuppSaveError('Supplier name is required.'); return; }
    try {
      setSuppSaveLoading(true);
      setSuppSaveError('');
      if (suppEditTarget) {
        await updateSupplier(suppEditTarget._id, suppForm);
      } else {
        await createSupplier(suppForm);
      }
      setSuppDialogOpen(false);
      await loadSuppliers();
    } catch (err) {
      setSuppSaveError(err.response?.data?.message || 'Failed to save supplier.');
    } finally {
      setSuppSaveLoading(false);
    }
  };

  const handleDeleteSupplier = async () => {
    try {
      setDeleteSuppLoading(true);
      await deleteSupplier(deleteSuppTarget._id);
      setDeleteSuppTarget(null);
      await loadSuppliers();
    } catch (err) {
      setSuppError(err.response?.data?.message || 'Failed to delete supplier.');
    } finally {
      setDeleteSuppLoading(false);
    }
  };

  /* ── Create PO ── */
  const openCreatePO = async () => {
    setPoForm({ supplierId: '', notes: '', expectedDate: '', items: [{ productId: '', quantity: '', unitCost: '' }] });
    setPoSaveError('');
    if (products.length === 0) {
      try { const r = await fetchProducts(); setProducts(r.data?.products || r.data || []); } catch { setProducts([]); }
    }
    setPoDialogOpen(true);
  };

  const addPOLine = () => setPoForm((f) => ({ ...f, items: [...f.items, { productId: '', quantity: '', unitCost: '' }] }));
  const removePOLine = (i) => setPoForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const updatePOLine = (i, field, val) => setPoForm((f) => ({
    ...f,
    items: f.items.map((item, idx) => idx === i ? { ...item, [field]: val } : item),
  }));

  const handleCreatePO = async () => {
    if (!poForm.supplierId) { setPoSaveError('Select a supplier.'); return; }
    const validItems = poForm.items.filter((it) => it.productId && it.quantity && Number(it.quantity) > 0);
    if (validItems.length === 0) { setPoSaveError('Add at least one item.'); return; }
    try {
      setPoSaveLoading(true);
      setPoSaveError('');
      await createPurchaseOrder({
        supplierId: poForm.supplierId,
        notes: poForm.notes,
        expectedDate: poForm.expectedDate || undefined,
        items: validItems.map((it) => ({
          productId: it.productId,
          quantity: Number(it.quantity),
          unitCost: it.unitCost ? Number(it.unitCost) : undefined,
        })),
      });
      setPoDialogOpen(false);
      await loadPOs();
    } catch (err) {
      setPoSaveError(err.response?.data?.message || 'Failed to create purchase order.');
    } finally {
      setPoSaveLoading(false);
    }
  };

  /* ── Receive PO ── */
  const openReceive = (po) => {
    setReceivePO(po);
    setReceiveItems((po.items || []).map((it) => ({ ...it, receivedQty: it.quantity || 0 })));
    setReceiveError('');
    setReceiveDialogOpen(true);
  };

  const handleReceive = async () => {
    try {
      setReceiveLoading(true);
      setReceiveError('');
      await receivePurchaseOrder(receivePO._id, {
        items: receiveItems.map((it) => ({
          productId: it.productId || it.product?._id,
          receivedQuantity: Number(it.receivedQty),
        })),
      });
      setReceiveDialogOpen(false);
      await loadPOs();
    } catch (err) {
      setReceiveError(err.response?.data?.message || 'Failed to receive purchase order.');
    } finally {
      setReceiveLoading(false);
    }
  };

  const handleMarkSent = async (po) => {
    try { await updatePOStatus(po._id, { status: 'sent' }); await loadPOs(); }
    catch (err) { setPoError(err.response?.data?.message || 'Failed to update status.'); }
  };

  /* ════════════════ RENDER ════════════════ */
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" fontWeight={800} color="#1e293b" gutterBottom>Supplier Management</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>Manage suppliers and purchase orders</Typography>

      <Paper sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid #e2e8f0', px: 2, pt: 1 }}>
          <Tab label="Suppliers" />
          <Tab label="Purchase Orders" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* ─── SUPPLIERS TAB ─── */}
          <TabPanel value={tab} index={0}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={700}>{suppliers.length} Suppliers</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Refresh"><IconButton onClick={loadSuppliers} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}><Refresh /></IconButton></Tooltip>
                <Button variant="contained" startIcon={<Add />} onClick={openCreateSupplier}
                  sx={{ borderRadius: 2, background: 'linear-gradient(135deg,#667eea,#764ba2)' }}>
                  Add Supplier
                </Button>
              </Box>
            </Box>

            {suppError && <Alert severity="error" sx={{ mb: 2 }}>{suppError}</Alert>}

            {suppLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      {['Name', 'Contact', 'Phone', 'Email', 'Payment Terms', 'Status', 'Actions'].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, color: '#64748b' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {suppliers.length === 0 ? (
                      <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, color: '#94a3b8' }}>No suppliers yet.</TableCell></TableRow>
                    ) : suppliers.map((s) => (
                      <TableRow key={s._id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LocalShipping sx={{ fontSize: 16, color: '#667eea' }} />
                            <Typography fontWeight={600} variant="body2">{s.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{s.contactPerson || '—'}</TableCell>
                        <TableCell>{s.phone || '—'}</TableCell>
                        <TableCell><Typography variant="caption">{s.email || '—'}</Typography></TableCell>
                        <TableCell>{s.paymentTerms || '—'}</TableCell>
                        <TableCell>
                          <Chip label={s.active !== false ? 'Active' : 'Inactive'} color={s.active !== false ? 'success' : 'default'} size="small" sx={{ fontWeight: 700 }} />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton size="small" onClick={() => openEditSupplier(s)}><Edit fontSize="small" color="primary" /></IconButton>
                            <IconButton size="small" onClick={() => setDeleteSuppTarget(s)}><Delete fontSize="small" color="error" /></IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>

          {/* ─── PURCHASE ORDERS TAB ─── */}
          <TabPanel value={tab} index={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>Supplier</InputLabel>
                  <Select value={poSuppFilter} label="Supplier" onChange={(e) => setPoSuppFilter(e.target.value)}>
                    <MenuItem value="">All Suppliers</MenuItem>
                    {suppliers.map((s) => <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Status</InputLabel>
                  <Select value={poStatusFilter} label="Status" onChange={(e) => setPoStatusFilter(e.target.value)}>
                    <MenuItem value="">All Statuses</MenuItem>
                    <MenuItem value="draft">Draft</MenuItem>
                    <MenuItem value="sent">Sent</MenuItem>
                    <MenuItem value="partial">Partial</MenuItem>
                    <MenuItem value="received">Received</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title="Refresh"><IconButton onClick={loadPOs} sx={{ border: '1px solid #e2e8f0', borderRadius: 2 }}><Refresh /></IconButton></Tooltip>
                <Button variant="contained" startIcon={<Add />} onClick={openCreatePO}
                  sx={{ borderRadius: 2, background: 'linear-gradient(135deg,#667eea,#764ba2)' }}>
                  Create PO
                </Button>
              </Box>
            </Box>

            {poError && <Alert severity="error" sx={{ mb: 2 }}>{poError}</Alert>}

            {poLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      {['PO Number', 'Supplier', 'Items', 'Total Cost', 'Status', 'Ordered', 'Expected', 'Actions'].map((h) => (
                        <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, color: '#64748b' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pos.length === 0 ? (
                      <TableRow><TableCell colSpan={8} align="center" sx={{ py: 6, color: '#94a3b8' }}>No purchase orders found.</TableCell></TableRow>
                    ) : pos.map((po) => (
                      <TableRow key={po._id} hover>
                        <TableCell><Typography variant="caption" fontFamily="monospace" fontWeight={700}>{po.poNumber || po._id?.slice(-8).toUpperCase()}</Typography></TableCell>
                        <TableCell>{po.supplier?.name || suppliers.find((s) => s._id === po.supplierId)?.name || '—'}</TableCell>
                        <TableCell>{po.items?.length ?? 0}</TableCell>
                        <TableCell><Typography fontWeight={700}>{KES(po.totalCost || po.items?.reduce((s, it) => s + (it.quantity || 0) * (it.unitCost || 0), 0))}</Typography></TableCell>
                        <TableCell>
                          <Chip label={(po.status || 'draft').charAt(0).toUpperCase() + (po.status || 'draft').slice(1)} color={PO_STATUS_COLORS[po.status] || 'default'} size="small" sx={{ fontWeight: 700 }} />
                        </TableCell>
                        <TableCell>{fmt(po.createdAt)}</TableCell>
                        <TableCell>{fmt(po.expectedDate)}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {['draft', 'sent', 'partial'].includes(po.status) && (
                              <Tooltip title="Receive stock">
                                <Button size="small" variant="contained" startIcon={<Check />} onClick={() => openReceive(po)}
                                  sx={{ borderRadius: 1.5, fontSize: 11, py: 0.3, px: 1, background: 'linear-gradient(135deg,#22c55e,#16a34a)' }}>
                                  Receive
                                </Button>
                              </Tooltip>
                            )}
                            {po.status === 'draft' && (
                              <Tooltip title="Mark as sent">
                                <Button size="small" variant="outlined" onClick={() => handleMarkSent(po)}
                                  sx={{ borderRadius: 1.5, fontSize: 11, py: 0.3, px: 1 }}>
                                  Mark Sent
                                </Button>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>
        </Box>
      </Paper>

      {/* ─── Supplier Create/Edit Dialog ─── */}
      <Dialog open={suppDialogOpen} onClose={() => setSuppDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={800}>{suppEditTarget ? 'Edit Supplier' : 'Add Supplier'}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Supplier Name *" value={suppForm.name} onChange={(e) => setSuppForm({ ...suppForm, name: e.target.value })} fullWidth size="small" />
            <TextField label="Contact Person" value={suppForm.contactPerson} onChange={(e) => setSuppForm({ ...suppForm, contactPerson: e.target.value })} fullWidth size="small" />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Phone" value={suppForm.phone} onChange={(e) => setSuppForm({ ...suppForm, phone: e.target.value })} fullWidth size="small" />
              <TextField label="Email" value={suppForm.email} onChange={(e) => setSuppForm({ ...suppForm, email: e.target.value })} fullWidth size="small" />
            </Box>
            <TextField label="Payment Terms" value={suppForm.paymentTerms} onChange={(e) => setSuppForm({ ...suppForm, paymentTerms: e.target.value })} fullWidth size="small" placeholder="e.g. Net 30, Cash on delivery" />
            {suppSaveError && <Alert severity="error">{suppSaveError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSuppDialogOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveSupplier} disabled={suppSaveLoading}
            sx={{ borderRadius: 2, background: 'linear-gradient(135deg,#667eea,#764ba2)' }}>
            {suppSaveLoading ? <CircularProgress size={18} /> : suppEditTarget ? 'Save Changes' : 'Add Supplier'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Delete Supplier Confirm ─── */}
      <Dialog open={!!deleteSuppTarget} onClose={() => setDeleteSuppTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={800} color="error">Delete Supplier?</DialogTitle>
        <DialogContent>
          <Typography>Delete <strong>{deleteSuppTarget?.name}</strong>? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteSuppTarget(null)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteSupplier} disabled={deleteSuppLoading} sx={{ borderRadius: 2 }}>
            {deleteSuppLoading ? <CircularProgress size={18} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Create PO Dialog ─── */}
      <Dialog open={poDialogOpen} onClose={() => setPoDialogOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={800} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Receipt /> Create Purchase Order
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Supplier *</InputLabel>
                <Select value={poForm.supplierId} label="Supplier *" onChange={(e) => setPoForm({ ...poForm, supplierId: e.target.value })}>
                  {suppliers.map((s) => <MenuItem key={s._id} value={s._id}>{s.name}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField
                type="date"
                label="Expected Date"
                value={poForm.expectedDate}
                onChange={(e) => setPoForm({ ...poForm, expectedDate: e.target.value })}
                size="small"
                InputLabelProps={{ shrink: true }}
                sx={{ flex: 1 }}
              />
            </Box>

            <Divider />
            <Typography variant="subtitle2" fontWeight={700}>Line Items</Typography>

            {poForm.items.map((item, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <FormControl size="small" sx={{ flex: 2 }}>
                  <InputLabel>Product</InputLabel>
                  <Select value={item.productId} label="Product" onChange={(e) => updatePOLine(i, 'productId', e.target.value)}>
                    {products.map((p) => <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  label="Qty"
                  type="number"
                  value={item.quantity}
                  onChange={(e) => updatePOLine(i, 'quantity', e.target.value)}
                  sx={{ width: 80 }}
                  inputProps={{ min: 1 }}
                />
                <TextField
                  size="small"
                  label="Unit Cost (KES)"
                  type="number"
                  value={item.unitCost}
                  onChange={(e) => updatePOLine(i, 'unitCost', e.target.value)}
                  sx={{ width: 130 }}
                />
                {poForm.items.length > 1 && (
                  <IconButton size="small" color="error" onClick={() => removePOLine(i)}><Delete fontSize="small" /></IconButton>
                )}
              </Box>
            ))}

            <Button startIcon={<Add />} onClick={addPOLine} variant="outlined" size="small" sx={{ borderRadius: 2, alignSelf: 'flex-start' }}>
              Add Item
            </Button>

            <TextField label="Notes" value={poForm.notes} onChange={(e) => setPoForm({ ...poForm, notes: e.target.value })} fullWidth size="small" multiline rows={2} />
            {poSaveError && <Alert severity="error">{poSaveError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPoDialogOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleCreatePO} disabled={poSaveLoading}
            sx={{ borderRadius: 2, background: 'linear-gradient(135deg,#667eea,#764ba2)' }}>
            {poSaveLoading ? <CircularProgress size={18} /> : 'Create PO'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Receive PO Dialog ─── */}
      <Dialog open={receiveDialogOpen} onClose={() => setReceiveDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle fontWeight={800}>Receive Stock — PO #{receivePO?.poNumber || receivePO?._id?.slice(-8).toUpperCase()}</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {receiveItems.map((item, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" fontWeight={600}>{item.productName || item.product?.name || `Item ${i + 1}`}</Typography>
                <Typography variant="caption" color="text.secondary">Ordered: {item.quantity}</Typography>
              </Box>
              <TextField
                size="small"
                label="Received Qty"
                type="number"
                value={item.receivedQty}
                onChange={(e) => setReceiveItems((prev) => prev.map((it, idx) => idx === i ? { ...it, receivedQty: e.target.value } : it))}
                sx={{ width: 130 }}
                inputProps={{ min: 0 }}
              />
            </Box>
          ))}
          {receiveError && <Alert severity="error" sx={{ mt: 1 }}>{receiveError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReceiveDialogOpen(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleReceive} disabled={receiveLoading}
            sx={{ borderRadius: 2, background: 'linear-gradient(135deg,#22c55e,#16a34a)' }}>
            {receiveLoading ? <CircularProgress size={18} /> : 'Confirm Receipt'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
